import {
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { parseTime } from "@shared/nz-time";
import { FieldError } from "./FieldError";
import styles from "./TimeInput.module.css";

const timeSuggestions: string[] = [];
for (let hour = 6; hour <= 26; hour++) {
  const displayHour = hour >= 24 ? hour - 24 : hour;
  const period = hour >= 12 && hour < 24 ? "PM" : "AM";
  const hour12 = displayHour === 0 ? 12 : displayHour > 12 ? displayHour - 12 : displayHour;
  timeSuggestions.push(`${hour12}:00 ${period}`, `${hour12}:30 ${period}`);
}

export interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  hasError?: boolean;
  error?: string;
  onValidityChange?: (valid: boolean) => void;
}

function displayValue(value: string): string {
  return parseTime(value) ?? value;
}

export function TimeInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  hasError,
  error,
  onValidityChange,
}: TimeInputProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() => displayValue(value));
  const [parseError, setParseError] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  useEffect(() => setInputValue(displayValue(value)), [value]);

  const filteredSuggestions = inputValue
    ? timeSuggestions.filter((time) =>
        time.toLowerCase().replace(/\s/g, "").includes(inputValue.toLowerCase().replace(/\s/g, "")),
      )
    : timeSuggestions;

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (!dropdownRef.current?.contains(target) && !inputRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const positionDropdown = (): void => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      const gap = 4;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const openAbove = spaceBelow < 120 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(80, Math.min(192, openAbove ? spaceAbove : spaceBelow));
      setDropdownStyle({
        position: "fixed",
        left: rect.left,
        top: openAbove ? Math.max(gap, rect.top - maxHeight - gap) : rect.bottom + gap,
        width: rect.width,
        maxHeight,
        zIndex: 1000,
      });
    };
    positionDropdown();
    window.addEventListener("resize", positionDropdown);
    window.addEventListener("scroll", positionDropdown, true);
    return () => {
      window.removeEventListener("resize", positionDropdown);
      window.removeEventListener("scroll", positionDropdown, true);
    };
  }, [isOpen, filteredSuggestions.length]);

  const selectTime = (time: string): void => {
    setInputValue(time);
    setParseError(false);
    onValidityChange?.(true);
    onChange(time);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleBlur = (): void => {
    const parsed = parseTime(inputValue);
    if (parsed === null) {
      setParseError(true);
      onValidityChange?.(false);
      return;
    }
    setParseError(false);
    onValidityChange?.(true);
    if (parsed !== inputValue) {
      setInputValue(parsed);
      onChange(parsed);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Escape") setIsOpen(false);
    if (event.key === "Enter" && filteredSuggestions.length > 0) {
      event.preventDefault();
      selectTime(filteredSuggestions[0]);
    }
  };

  const message = parseError ? error ?? "Enter a valid time (e.g. 8:00 PM) or leave blank" : error;
  const invalid = hasError || parseError || Boolean(error);

  return (
    <div className={styles.wrapper}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={isOpen && filteredSuggestions.length > 0}
        aria-controls={isOpen && filteredSuggestions.length > 0 ? listboxId : undefined}
        value={inputValue}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setInputValue(event.target.value);
          setParseError(false);
          setIsOpen(true);
          onChange(event.target.value);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        className={`${styles.input} ${invalid ? styles.invalid : ""}`}
      />
      <FieldError message={message} />
      {isOpen && filteredSuggestions.length > 0 && createPortal(
        <div
          ref={dropdownRef}
          id={listboxId}
          role="listbox"
          aria-label={`${ariaLabel ?? "Time"} suggestions`}
          style={dropdownStyle}
          className={styles.suggestions}
        >
          {filteredSuggestions.map((time) => (
            <div
              key={time}
              role="option"
              aria-selected={time === inputValue}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectTime(time)}
              className={styles.option}
            >
              {time}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
